import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/auth-context";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FIGHTER_EDIT_PATH, PROFILE_PATH } from "../App";
import {
    fighterEntryDisplayMapping,
    fighterRequestStatus,
    fighterRequestType,
    paymentMethod,
    weightClassesName,
} from "../constants";
import toast from "react-hot-toast";
import { Check, Clipboard, CoinsIcon, QrCode, X } from "lucide-react";
import { LiaWeightSolid } from "react-icons/lia";
import Preview from "../components/preview";
import WeightClassPicker from "../components/weight-class-picker";
import {
    eventFightersCollection,
    fetchActiveTokensForFighter,
    fetchAgreementForEvent,
    fetchEvent,
    fighterRequestsCollection,
    logError,
    recordAgreement,
    registerEvent,
    requestWeightChange,
    updateRegisteredEvent,
} from "../utils/firebase";
import EventRegisterAgreementModal from "../components/event-register-agreement-modal";
import {
    and,
    doc,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    Timestamp,
    where,
} from "firebase/firestore";
import {
    extractTimestamp,
    formatFirestoreTimestamp,
    splitWeightClassLabel,
} from "../utils/common";

import Countdown from "../components/count-down";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "react-i18next";


export default function RegisterEvent() {
    const { t } = useTranslation();
    const { id } = useParams();
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [request, setRequest] = useState(null);
    // True when the fighter was refunded for this event — lets them register
    // again even if their last request is "approved".
    const [isRefundedForEvent, setIsRefundedForEvent] = useState(false);
    const [entry, setEntry] = useState("");
    // The weight class declared for this event, and what it was when the page
    // loaded — so a change reads as a change.
    const [eventWeightClass, setEventWeightClass] = useState("");
    const [baselineWeightClass, setBaselineWeightClass] = useState("");
    const [weightChangeOpen, setWeightChangeOpen] = useState(false);
    const [newWeightClass, setNewWeightClass] = useState("");
    const [submittingWeight, setSubmittingWeight] = useState(false);
    const [pendingWeightRequest, setPendingWeightRequest] = useState(null);
    // Joining goes through a confirm step so the weight class is looked at
    // rather than inherited silently from the profile.
    const [joinConfirmOpen, setJoinConfirmOpen] = useState(false);
    const [joinCountdown, setJoinCountdown] = useState(0);
    const [joining, setJoining] = useState(false);
    const [receipt, setReceipt] = useState("");
    const [loading, setLoading] = useState(true);
    const exampleModal = useRef(null);
    const qrModal = useRef(null);

    const [expired, setExpired] = useState();
    const [agreement, setAgreement] = useState(null);
    const [agreementModalOpen, setAgreementModalOpen] = useState(false);
    const [recordingAgreement, setRecordingAgreement] = useState(false);
    const [activeTokens, setActiveTokens] = useState([]);
    const [useToken, setUseToken] = useState(false);
    const loadedKeyRef = useRef(null);

    if (!id) {
        navigate("/404");
    }

    const handleClose = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/");
        }
    };
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) {
            setReceipt(null);
            return;
        }

        // Get file extension
        const fileName = file.name;
        const fileExt = fileName.split(".").pop().toLowerCase();

        // List of allowed extensions
        const allowedExts =
            e.target.name === "receipt"
                ? ["pdf", "jpg", "jpeg", "png", "webp"]
                : ["jpg", "jpeg", "png", "webp"];

        if (!allowedExts.includes(fileExt)) {
            toast.error(
                `Please upload a valid file type: ${allowedExts.join(", ")}`
            );
            e.target.value = ""; // Clear the input
            return;
        }

        // Check file size (e.g., 5MB max)
        const maxSize = 25 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            toast.error("File size too large (max 25MB)");
            e.target.value = "";
            return;
        }

        // Process the valid file
        setReceipt(file);
    };

    const handleRequestWeightChange = async () => {
        const next = newWeightClass || baselineWeightClass;
        if (!next) return toast.error("Pick a weight class first.");
        if (next === baselineWeightClass) {
            return toast.error("That's the class you're already registered in.");
        }
        setSubmittingWeight(true);
        try {
            await requestWeightChange(currentUser, id, next, {
                previousWeightClass: baselineWeightClass || null,
                eventName: event?.data?.name ?? event?.name ?? null,
            });
            setPendingWeightRequest({ data: { eventWeightClass: next } });
            setWeightChangeOpen(false);
            toast.success("Sent for review.");
        } catch (error) {
            toast.error(error?.message || "Something went wrong.");
        } finally {
            setSubmittingWeight(false);
        }
    };

    // Everything that can be judged before the fighter confirms their class.
    // Run on the page button so a missing receipt is caught there rather than
    // after they've sat through the confirm step.
    const validateJoin = ({ requireWeightClass = true } = {}) => {
        if (
            !!expired ||
            (!!event?.registerExpiredDateTime?.toDate() &&
                event?.registerExpiredDateTime?.toDate() < new Date())
        ) {
            logError(currentUser.uid, "registerEvent", new Error(`Registration closed at ${event?.registerExpiredDateTime?.toDate()}, user current time is ${new Date()}`));
            toast.error(t("eventRegister.errors.registrationClosed"));
            return false;
        }
        if (
            request?.status === fighterRequestStatus.approved &&
            !isRefundedForEvent
        ) {
            toast.error(t("eventRegister.errors.alreadyRegistered"));
            return false;
        }
        if (!agreement) {
            setAgreementModalOpen(true);
            toast.error(t("eventRegister.errors.agreementRequired"));
            return false;
        }
        if (!entry || (requireWeightClass && !eventWeightClass)) {
            toast.error(t("eventRegister.errors.missingFields"));
            return false;
        }
        const method = useToken ? paymentMethod.token : paymentMethod.slip;
        if (method === paymentMethod.token && !(activeTokens[0]?.id ?? null)) {
            toast.error(t("eventRegister.errors.noTokenAvailable"));
            return false;
        }
        if (method === paymentMethod.slip && !receipt) {
            toast.error(t("eventRegister.errors.missingFields"));
            return false;
        }
        return true;
    };

    const handleJoinClick = () => {
        // The class isn't required yet — it's the thing the popup asks for.
        if (!validateJoin({ requireWeightClass: false })) return;
        setJoinConfirmOpen(true);
    };

    const handleSubmit = async () => {
        if (joining) return;
        if (!validateJoin()) return;
        const method = useToken ? paymentMethod.token : paymentMethod.slip;
        const tokenId = useToken ? activeTokens[0]?.id ?? null : null;
        const eventId = id;
        const fighterId = currentUser.uid;

        setJoining(true);
        try {
            if (!request) {
                await toast.promise(
                    registerEvent(
                        currentUser,
                        eventId,
                        fighterId,
                        currentUser.email,
                        entry,
                        eventWeightClass,
                        receipt,
                        { paymentMethod: method, tokenId }
                    ),
                    {
                        loading: "Submitting",
                        success: () => {
                            navigate(`${PROFILE_PATH}?tab=registeredEvent`);
                            return "Successful";
                        },
                        error: (error) =>
                            error?.message || "Something went wrong.",
                    }
                );
            } else {
                await toast.promise(
                    updateRegisteredEvent(
                        request.id,
                        eventId,
                        fighterId,
                        entry,
                        eventWeightClass,
                        receipt,
                        { paymentMethod: method, tokenId }
                    ),
                    {
                        loading: "Submitting",
                        success: () => {
                            navigate(`${PROFILE_PATH}?tab=registeredEvent`);
                            return "Successful";
                        },
                        error: (error) =>
                            error?.message || "Something went wrong.",
                    }
                );
            }
            setJoinConfirmOpen(false);
        } catch (error) {
            // toast.promise has already shown the message. Leave the popup
            // open so a retry doesn't mean walking the whole form again.
            console.error("Error on handleSubmit(): ", error);
        } finally {
            setJoining(false);
        }
    };

    // Countdown on the confirm popup's submit. Three seconds is a deliberate
    // beat: the weight class is the one field that silently carries over from
    // the profile, and a mis-declared class voids a bout.
    useEffect(() => {
        if (!joinConfirmOpen) return undefined;
        setJoinCountdown(3);
        const tick = setInterval(
            () => setJoinCountdown((n) => (n > 0 ? n - 1 : 0)),
            1000
        );
        return () => clearInterval(tick);
    }, [joinConfirmOpen]);

    // Reconcile useToken state when the fighter has no active tokens. Skip the
    // override if they're already APPROVED via token (locked-in state) — there
    // the toggle is informational only.
    useEffect(() => {
        if (!request) return;
        const requestPaidByToken =
            request?.data?.paymentMethod === paymentMethod.token;
        const isApprovedTokenPayment =
            request?.status === fighterRequestStatus.approved &&
            requestPaidByToken;
        if (
            useToken &&
            activeTokens.length === 0 &&
            !isApprovedTokenPayment
        ) {
            setUseToken(false);
        }
    }, [useToken, activeTokens.length, request]);

    const handleAgreementCancel = () => {
        setAgreementModalOpen(false);
        handleClose();
    };

    const handleAgreementAccept = async () => {
        if (recordingAgreement) return;
        setRecordingAgreement(true);
        try {
            const result = await recordAgreement(currentUser.uid, id);
            if (!result || result.success === false) {
                throw new Error(result?.error || "Failed to record agreement");
            }
            setAgreement({
                uid: currentUser.uid,
                eventId: id,
                agreedAt: new Date(),
                userAgent: navigator.userAgent,
                agreementVersion: result?.agreementVersion ?? null,
            });
            setAgreementModalOpen(false);
        } catch (error) {
            toast.error(error?.message || "Failed to record agreement");
        } finally {
            setRecordingAgreement(false);
        }
    };

    useEffect(() => {
        const key = `${currentUser?.uid ?? ""}|${id}`;
        if (!currentUser?.uid || !id) return;
        if (loadedKeyRef.current === key) return;
        loadedKeyRef.current = key;
        (async () => {
            const fetchedEvent = await fetchEvent({
                id: id,
            });

            if (!!fetchedEvent.data) {
                setEvent({
                    ...fetchedEvent.data,
                    dateTime: !fetchedEvent.data.dateTime?._seconds
                        ? null
                        : Timestamp.fromMillis(
                            fetchedEvent.data.dateTime?._seconds * 1000
                        ),
                    registerExpiredDateTime: !fetchedEvent.data
                        .registerExpiredDateTime?._seconds
                        ? null
                        : Timestamp.fromMillis(
                            fetchedEvent.data.registerExpiredDateTime
                                ?._seconds * 1000
                        ),
                    createdAt: !fetchedEvent.data.createdAt?._seconds
                        ? null
                        : Timestamp.fromMillis(
                            fetchedEvent.data.createdAt?._seconds * 1000
                        ),
                    updateAt: !fetchedEvent.data.updateAt?._seconds
                        ? null
                        : Timestamp.fromMillis(
                            fetchedEvent.data.updateAt?._seconds * 1000
                        ),
                });
            } else {
                navigate("/404");
            }

            const q = query(
                fighterRequestsCollection,
                and(
                    where("uid", "==", currentUser?.uid),
                    where("eventId", "==", id),
                    where("type", "==", fighterRequestType.registerEvent)
                ),
                limit(1),
                orderBy("updateAt", "desc")
            );

            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                const requestData = snapshot.docs[0];
                const requestPayload = requestData.data();
                // Defensive: legacy / manually-seeded docs may omit `data`
                const reqData = requestPayload?.data ?? {};
                setRequest({
                    id: requestData.id,
                    ...requestPayload,
                });
                setEntry(reqData.entry ?? "");
                const declared =
                    reqData.eventWeightClass ||
                    currentUser.fighter?.weightClass ||
                    "";
                setEventWeightClass(declared);
                setBaselineWeightClass(declared);
                setReceipt(reqData.receipt ?? "");
                setUseToken(reqData.paymentMethod === paymentMethod.token);
            } else {
                // No prior request — start from the profile's class.
                const declared = currentUser.fighter?.weightClass || "";
                setEventWeightClass(declared);
                setBaselineWeightClass(declared);
            }

            // Surface an undecided weight-change request so the fighter isn't
            // left wondering whether it went through.
            try {
                const wcSnap = await getDocs(
                    query(
                        fighterRequestsCollection,
                        and(
                            where("uid", "==", currentUser?.uid),
                            where("eventId", "==", id),
                            where(
                                "type",
                                "==",
                                fighterRequestType.weightChange
                            ),
                            where(
                                "status",
                                "==",
                                fighterRequestStatus.pending
                            )
                        ),
                        limit(1)
                    )
                );
                setPendingWeightRequest(
                    wcSnap.empty
                        ? null
                        : { id: wcSnap.docs[0].id, ...wcSnap.docs[0].data() }
                );
            } catch (wcErr) {
                console.error("Failed to load weight change request:", wcErr);
            }

            // A fighter refunded for this event may register again even if their
            // last request is "approved" — the refund returned their credit, so
            // unlock the form. Approval converges to a SELF_SLIP/SELF_TOKEN
            // rejoin (keeping any unused credit).
            try {
                const efSnap = await getDoc(
                    doc(eventFightersCollection, `${id}_${currentUser.uid}`)
                );
                setIsRefundedForEvent(
                    efSnap.exists() && efSnap.data()?.isRefunded === true
                );
            } catch (efErr) {
                console.error("Failed to check refund state:", efErr);
            }

            setExpired(
                fetchedEvent.data.isTest === true ||
                (!!!fetchedEvent.data.registerExpiredDateTime?._seconds &&
                Timestamp.fromMillis(
                    fetchedEvent.data.registerExpiredDateTime?._seconds *
                    1000
                ).toDate() < new Date())
            );

            if (currentUser?.role?.toLowerCase() === "fighter") {
                const existingAgreement = await fetchAgreementForEvent(
                    currentUser.uid,
                    id
                );
                setAgreement(existingAgreement);
                if (!existingAgreement) {
                    setAgreementModalOpen(true);
                }

                const tokens = await fetchActiveTokensForFighter(
                    currentUser.uid
                );
                setActiveTokens(tokens);
            }

            setLoading(false);
        })();
    }, [currentUser?.uid, id, navigate]);

    if (loading) {
        return (
            <div className="w-full min-h-[calc(100vh-6.5rem)] flex-center">
                <div className="loading loading-md loading-bars" />
            </div>
        );
    }

    if (currentUser.role?.toLowerCase() === "admin") {
        return (
            <div className="w-full min-h-[calc(100vh-6.5rem)] flex-center gap-4 p-4">
                <p className="font-bold text-center">
                    {t("eventRegister.admin.message")}
                </p>
                <button className="btn btn-outline" onClick={handleClose}>
                    {t("eventRegister.admin.goBack")}
                </button>
            </div>
        );
    }

    if (currentUser.role?.toLowerCase() !== "fighter") {
        return (
            <div className="w-full min-h-[calc(100vh-6.5rem)] flex-center gap-4 p-4">
                <p className="font-bold text-center">
                    {t("eventRegister.notFighter.message")}
                </p>
                <div className="flex flex-col-reverse w-full p-4 md:w-auto md:flex-row gap-4 justify-center items-center">
                    <button
                        className="btn btn-outline w-full"
                        onClick={handleClose}
                    >
                        {t("eventRegister.notFighter.goBack")}
                    </button>
                    <Link className="btn btn-primary w-full" to={PROFILE_PATH}>
                        {t("eventRegister.notFighter.registerNow")}
                    </Link>
                </div>
            </div>
        );
    }

    const isJoined =
        request?.status === fighterRequestStatus.approved &&
        !isRefundedForEvent;

    const selectedClassLabel = splitWeightClassLabel(eventWeightClass);

    return (
        <>
            <div className="w-full min-h-[calc(100vh-6.5rem)] flex-center p-4 max-[500px]:p-2 max-[500px]:pt-12">
                <button
                    className="btn btn-square btn-ghost fixed top-4 right-4 max-[500px]:top-2 max-[500px]:right-2"
                    onClick={handleClose}
                >
                    <X />
                </button>
                <div className="max-w-[30rem] flex-center justify-start gap-4">
                    <div className="flex-center items-start gap-2 !text-white w-full">
                        {isJoined && (
                            <div className="border border-success p-3 w-full bg-success/10">
                                <p className="font-bold">
                                    {t("eventRegister.joinedBanner.title")}
                                </p>
                                <p className="text-sm opacity-80">
                                    {t("eventRegister.joinedBanner.help")}
                                </p>
                            </div>
                        )}
                        {isRefundedForEvent && (
                            <div className="border border-warning p-3 w-full bg-warning/10">
                                <p className="font-bold">
                                    You were refunded for this event
                                </p>
                                <p className="text-sm opacity-80">
                                    You can register again below — pay by slip, or
                                    use a credit if you have one. Paying by slip
                                    keeps your refund credit for a future event.
                                </p>
                            </div>
                        )}
                        <div className="flex-center w-full">
                            <div className="flex flex-col gap-2 w-full">
                                <p className="opacity-80">
                                    {t("eventRegister.registerBefore")}
                                </p>
                                <div className="border p-4 w-full flex-center">
                                    <Countdown
                                        className={"w-full"}
                                        dueDate={event.registerExpiredDateTime?.toDate()}
                                    />
                                </div>
                            </div>
                        </div>
                        {(activeTokens.length > 0 || (isJoined && useToken)) && (
                            <div
                                className={twMerge(
                                    "border p-3 w-full flex flex-col gap-2 transition-colors",
                                    useToken
                                        ? "bg-white text-black border-white"
                                        : "bg-base-200"
                                )}
                            >
                                <div className="flex flex-row items-start gap-2">
                                    <CoinsIcon
                                        className={twMerge(
                                            "mt-1 shrink-0",
                                            useToken
                                                ? "text-yellow-500"
                                                : "text-yellow-300"
                                        )}
                                    />
                                    <div className="flex-1">
                                        <p className="font-bold">
                                            {isJoined && useToken
                                                ? t(
                                                      "eventRegister.payment.tokenUsedForThis"
                                                  )
                                                : t(
                                                      "eventRegister.payment.tokenAvailable",
                                                      {
                                                          count: activeTokens.length,
                                                      }
                                                  )}
                                        </p>
                                        <p className="text-sm opacity-80">
                                            {isJoined && useToken
                                                ? t(
                                                      "eventRegister.payment.tokenUsedHelp"
                                                  )
                                                : t(
                                                      "eventRegister.payment.tokenHelp"
                                                  )}
                                        </p>
                                    </div>
                                </div>
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-primary"
                                        checked={useToken}
                                        disabled={
                                            isJoined ||
                                            activeTokens.length === 0
                                        }
                                        onChange={(e) =>
                                            setUseToken(e.target.checked)
                                        }
                                    />
                                    <span
                                        className={twMerge(
                                            "label-text inline-flex items-center gap-1",
                                            useToken &&
                                            "!text-success font-semibold"
                                        )}
                                    >
                                        {useToken && (
                                            <Check
                                                size={16}
                                                strokeWidth={3}
                                                className="text-success shrink-0"
                                            />
                                        )}
                                        {t(
                                            useToken
                                                ? "eventRegister.payment.usingTokenNow"
                                                : "eventRegister.payment.useTokenInstead"
                                        )}
                                    </span>
                                </label>
                            </div>
                        )}
                        {!useToken && (
                            <>
                                <p className="opacity-80">
                                    {t("eventRegister.uploadProof")}
                                </p>
                                <p className="opacity-80"></p>
                                <div className="border p-2 w-full">
                                    <div className="flex flex-col w-full">
                                        <p className="font-bold text-end">
                                            {t(
                                                "eventRegister.bankDetails.title"
                                            )}
                                        </p>
                                        <div className="divider m-0" />
                                        <table className="table table-xs">
                                            <tbody>
                                                <tr>
                                                    <th className="text-start">
                                                        {t(
                                                            "eventRegister.bankDetails.name"
                                                        )}
                                                    </th>
                                                    <td>BBL TRADING</td>
                                                </tr>
                                                <tr>
                                                    <th className="text-start">
                                                        {t(
                                                            "eventRegister.bankDetails.amount"
                                                        )}
                                                    </th>
                                                    <td className="font-bold">
                                                        MYR 80.00
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="text-start">
                                                        {t(
                                                            "eventRegister.bankDetails.bankNumber"
                                                        )}
                                                    </th>
                                                    <td>
                                                        <div className="flex flex-row justify-between items-center">
                                                            <p>512754604183</p>
                                                            <button
                                                                className="btn btn-xs btn-square btn-primary"
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(
                                                                        "512754604183"
                                                                    );
                                                                    toast(
                                                                        t(
                                                                            "eventRegister.bankDetails.copied"
                                                                        )
                                                                    );
                                                                }}
                                                            >
                                                                <Clipboard
                                                                    size={18}
                                                                />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="flex gap-4 w-full">
                                    <button
                                        className="btn btn-outline flex-1 btn-sm"
                                        onClick={() =>
                                            exampleModal?.current?.showModal()
                                        }
                                    >
                                        {t("eventRegister.buttons.example")}
                                    </button>
                                    <button
                                        className="btn btn-primary flex-1 btn-sm"
                                        onClick={() =>
                                            qrModal?.current?.showModal()
                                        }
                                    >
                                        {t("eventRegister.buttons.qr")}
                                        <QrCode size={16} />
                                    </button>
                                </div>
                                <div className="flex-center w-full gap-2">
                                    {!receipt ? null : (
                                        <Preview
                                            className={"h-52 overflow-auto"}
                                            src={receipt}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    {!useToken && (
                        <input
                            type="file"
                            name="receipt"
                            className="file-input file-input-bordered w-full border-current"
                            accept="image/*,application/pdf"
                            disabled={isJoined}
                            onChange={handleFileChange}
                        />
                    )}
                    <p className="w-full opacity-80">
                        {t("eventRegister.selectEntry.title")}
                    </p>
                    <select
                        name="entry"
                        className="input input-bordered w-full"
                        value={entry}
                        disabled={isJoined}
                        onChange={(e) => setEntry(e.target.value)}
                    >
                        <option defaultChecked disabled value="">
                            {t("eventRegister.selectEntry.placeholder")}
                        </option>
                        {(() => {
                            const allowed =
                                Array.isArray(event?.allowedEntries) &&
                                    event.allowedEntries.length > 0
                                    ? event.allowedEntries
                                    : Object.keys(fighterEntryDisplayMapping);
                            return allowed.map((key, index) => {
                                const label =
                                    fighterEntryDisplayMapping[key] ?? key;
                                const win =
                                    currentUser.fighter.records?.[key]?.win;
                                const loss =
                                    currentUser.fighter.records?.[key]?.loss;
                                const draw =
                                    currentUser.fighter.records?.[key]?.draw;
                                return (
                                    <option key={index} value={key}>
                                        {label}
                                        {win
                                            ? ` (Win: ${win}, Loss: ${loss}, Draw: ${draw})`
                                            : ""}
                                    </option>
                                );
                            });
                        })()}
                    </select>
                    <p className="w-full opacity-80">
                        {t("eventRegister.selectWeightClass.title")}
                    </p>
                    {/* Read-only here: the class is confirmed in the join
                        popup, so there's one place it can be set and it can't
                        be filled in and then forgotten. */}
                    <div className="w-full border border-base-300 p-4 flex flex-col gap-1">
                        {eventWeightClass ? (
                            <>
                                <span className="flex flex-row items-baseline gap-2">
                                    <span className="font-mono font-bold">
                                        {eventWeightClass}
                                    </span>
                                    <span className="font-semibold">
                                        {weightClassesName[eventWeightClass]}
                                    </span>
                                </span>
                                {/* Same emphasis as the picker: the kg figure
                                    leads, the pound equivalent stays quiet. */}
                                <span className="block">
                                    {selectedClassLabel.lb && (
                                        <span className="text-xs opacity-60">
                                            {selectedClassLabel.lb}{" "}
                                        </span>
                                    )}
                                    {selectedClassLabel.kg && (
                                        <span className="text-sm font-bold tabular-nums">
                                            ({selectedClassLabel.kg})
                                        </span>
                                    )}
                                </span>
                            </>
                        ) : (
                            <span className="opacity-60">
                                {t("eventRegister.selectWeightClass.placeholder")}
                            </span>
                        )}
                        {!isJoined && (
                            <span className="text-xs opacity-60">
                                {t("eventRegister.confirmWeight.pageHint")}
                            </span>
                        )}
                    </div>
                    {isJoined &&
                        !event?.isTest &&
                        (pendingWeightRequest ? (
                            <div className="alert border border-base-300 rounded-none text-sm">
                                <LiaWeightSolid size={20} />
                                <span>
                                    Weight change to{" "}
                                    <b>
                                        {
                                            pendingWeightRequest?.data
                                                ?.eventWeightClass
                                        }
                                    </b>{" "}
                                    is waiting for review.
                                </span>
                                <button
                                    className="btn btn-ghost btn-xs"
                                    onClick={() => setWeightChangeOpen(true)}
                                >
                                    Change
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-outline w-full"
                                onClick={() => setWeightChangeOpen(true)}
                            >
                                <LiaWeightSolid size={20} />
                                Request a weight change
                            </button>
                        ))}

                    {!!request && (
                        <div className="flex flex-col gap-2 w-full">
                            <p>{t("eventRegister.comments.title")}</p>
                            <div className="flex-center items-start w-full gap-2 border p-4">
                                {!request?.comments ||
                                    request.comments.length < 1 ? (
                                    <div className="w-full h-full flex-center p-4">
                                        <p>
                                            {t(
                                                "eventRegister.comments.noComments"
                                            )}
                                        </p>
                                    </div>
                                ) : (
                                    request.comments.map((comment, index) => {
                                        const createdAt = !comment.createdAt
                                            ? null
                                            : extractTimestamp(
                                                comment.createdAt
                                            );
                                        return (
                                            <div
                                                className="w-full "
                                                key={index}
                                            >
                                                <div className="chat chat-start">
                                                    <div className="chat-bubble">
                                                        <p className="">
                                                            {comment.text ?? ""}
                                                        </p>
                                                    </div>
                                                    <div className="chat-footer">
                                                        {!!createdAt && (
                                                            <p className="text-sm opacity-80">
                                                                {createdAt.day}{" "}
                                                                {
                                                                    createdAt.month
                                                                }{" "}
                                                                {createdAt.year}
                                                                ,{" "}
                                                                {createdAt.hour}
                                                                :
                                                                {
                                                                    createdAt.minute
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}

                    <p className="w-full">
                        {t("eventRegister.profileOutdated")}{" "}
                        <Link
                            className="font-bold underline"
                            to={`${FIGHTER_EDIT_PATH}/${currentUser.uid}`}
                        >
                            {t("eventRegister.editNow")}
                        </Link>{" "}
                        {t("eventRegister.beforeJoin")}
                    </p>

                    <button
                        className={twMerge(
                            "btn w-full",
                            expired ||
                                !agreement ||
                                isJoined
                                ? "btn-disabled"
                                : "btn-primary"
                        )}
                        onClick={handleJoinClick}
                        disabled={!agreement || expired || isJoined}
                    >
                        {request?.status === fighterRequestStatus.approved
                            ? t("eventRegister.buttons.joined")
                            : expired
                                ? t("eventRegister.buttons.expired")
                                : !agreement
                                    ? t("eventRegister.buttons.agreementRequired")
                                    : !request
                                        ? t("eventRegister.buttons.joinNow")
                                        : t("eventRegister.buttons.resubmit")}
                    </button>
                    {!!request && (
                        <p className="w-full text-end text-sm opacity-80">
                            {t("eventRegister.lastUpdate")}
                            {formatFirestoreTimestamp(request.updateAt)}
                        </p>
                    )}
                </div>
            </div>
            {/* Receipt Example Pop Up Modal */}
            <dialog ref={exampleModal} className="modal">
                <div className="modal-box border">
                    <div className="flex flex-row justify-between items-center">
                        <h3 className="font-bold text-lg">
                            {t("eventRegister.modals.receiptExample.title")}
                        </h3>
                        <form method="dialog">
                            <button className="btn btn-ghost btn-square btn-sm">
                                <X />
                            </button>
                        </form>
                    </div>
                    <br />
                    <div className="flex-center w-full gap-2">
                        <div className="flex-center p-2 h-full w-full bg-white">
                            <img
                                className="w-full h-full object-contain object-center"
                                src="/assets/img/example_fighter_registration_fee.png"
                                alt=""
                            />
                        </div>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>

            <EventRegisterAgreementModal
                open={agreementModalOpen}
                onAgree={handleAgreementAccept}
                onCancel={handleAgreementCancel}
                submitting={recordingAgreement}
            />

            {/* Confirm the weight class before joining. It's the one field
                that carries over from the profile without being re-entered,
                so it gets its own deliberate step. */}
            {joinConfirmOpen && (
                <div className="modal modal-open">
                    <div className="modal-box border max-w-md">
                        <div className="flex flex-row justify-between items-center gap-2">
                            <h3 className="font-bold text-lg">
                                {t("eventRegister.confirmWeight.title")}
                            </h3>
                            <button
                                className="btn btn-ghost btn-square btn-sm"
                                disabled={joining}
                                onClick={() => setJoinConfirmOpen(false)}
                            >
                                <X />
                            </button>
                        </div>
                        <p className="text-sm opacity-70 mt-1">
                            {t("eventRegister.confirmWeight.subtitle", {
                                event:
                                    event?.name ||
                                    t(
                                        "eventRegister.confirmWeight.thisEvent"
                                    ),
                            })}
                        </p>

                        <div className="mt-4">
                            <WeightClassPicker
                                value={eventWeightClass}
                                baselineClass={baselineWeightClass}
                                disabled={joining}
                                onChange={setEventWeightClass}
                            />
                        </div>

                        <button
                            className={twMerge(
                                "btn w-full mt-5",
                                joinCountdown > 0 || !eventWeightClass
                                    ? "btn-disabled"
                                    : "btn-primary"
                            )}
                            disabled={
                                joining ||
                                joinCountdown > 0 ||
                                !eventWeightClass
                            }
                            onClick={handleSubmit}
                        >
                            {joining ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <>
                                    {!request
                                        ? t("eventRegister.buttons.joinNow")
                                        : t("eventRegister.buttons.resubmit")}
                                    {joinCountdown > 0 && (
                                        <span className="tabular-nums">
                                            (
                                            <span className="font-bold">
                                                {joinCountdown}
                                            </span>
                                            )
                                        </span>
                                    )}
                                </>
                            )}
                        </button>
                        <button
                            className="btn btn-ghost w-full mt-2"
                            disabled={joining}
                            onClick={() => setJoinConfirmOpen(false)}
                        >
                            {t("eventRegister.confirmWeight.cancel")}
                        </button>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={
                            joining ? undefined : () => setJoinConfirmOpen(false)
                        }
                    />
                </div>
            )}

            {/* Already registered, weight has moved. This can void a booked
                bout, so it goes through the request queue for an admin rather
                than writing the registration directly. */}
            {weightChangeOpen && (
                <div className="modal modal-open">
                    <div className="modal-box border max-w-md">
                        <div className="flex flex-row justify-between items-center">
                            <h3 className="font-bold text-lg">
                                Change weight class
                            </h3>
                            <button
                                className="btn btn-ghost btn-square btn-sm"
                                onClick={() => setWeightChangeOpen(false)}
                            >
                                <X />
                            </button>
                        </div>
                        <p className="text-sm opacity-70 mt-2">
                            You're registered in{" "}
                            <b>{baselineWeightClass || "no class"}</b>. An admin
                            reviews the change before it takes effect.
                        </p>
                        <div className="mt-4">
                            <WeightClassPicker
                                value={newWeightClass || baselineWeightClass}
                                baselineClass={baselineWeightClass}
                                onChange={setNewWeightClass}
                            />
                        </div>
                        <button
                            className="btn btn-primary w-full mt-5"
                            disabled={submittingWeight}
                            onClick={handleRequestWeightChange}
                        >
                            {submittingWeight ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                "Submit for review"
                            )}
                        </button>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setWeightChangeOpen(false)}
                    />
                </div>
            )}
            {/* Receipt QR Pop Up Modal */}
            <dialog ref={qrModal} className="modal">
                <div className="modal-box border">
                    <div className="flex flex-row justify-between items-center">
                        <h3 className="font-bold text-lg">
                            {t("eventRegister.modals.duitnow.title")}
                        </h3>
                        <form method="dialog">
                            <button className="btn btn-ghost btn-square btn-sm">
                                <X />
                            </button>
                        </form>
                    </div>
                    <br />
                    <div className="flex-center w-full gap-2">
                        <div className="flex-center p-2 h-full w-full bg-white">
                            <p className="font-bold text-black opacity-70 text-xs leading-none">
                                {t("eventRegister.bankDetails.title")}
                            </p>
                            <p className="font-bold text-black opacity-70 text-lg leading-none">
                                BBL TRADING
                            </p>
                            <img
                                className="w-full h-full max-w-48 aspect-square object-contain object-center"
                                src="/assets/img/mfl_bank_qr.jpeg"
                                alt=""
                            />
                        </div>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>
        </>
    );
}
